import { useRef, useState, useMemo } from 'react';
import { FontWeight, Text } from '../../tokens';
import { LiliActionButton } from '../LiliActionButton';
import { Spinner, SpinnerColor } from '../Spinner';
import { LiliFilePickerError, LiliFilePickerFileType } from '../LiliFilePicker';
import uploadIconUrl from '../../../assets/shared/ic-shared-upload.svg';
import uploadFailedIconUrl from '../../../assets/shared/ic-shared-upload-failed.svg';
import attachmentIconUrl from '../../../assets/shared/ic-shared-attachment.svg';
import { ReactComponent as TrashIcon } from '../../../assets/shared/ic-shared-trash.svg';

import type React from 'react';

export type LiliFileUploadState = 'default' | 'uploading' | 'uploaded' | 'failed' | 'disabled';

export interface LiliFileUploadTexts {
  addAttachment: string;
  addAttachmentMobile?: string;
  supportedFormats: string;
  uploadFailed: string;
  uploadNewFile: string;
}

export interface LiliFileUploadProps {
  id: string;
  state: LiliFileUploadState;
  file?: File | null;
  onFileSelect: (file: File) => void;
  onFileClear: () => void;
  onRetry?: () => void;
  onError?: (error: LiliFilePickerError) => void;
  acceptedTypes: LiliFilePickerFileType[];
  maxFileSizeInMb?: number;
  texts: LiliFileUploadTexts;
}

export const LiliFileUpload = ({
  state,
  file,
  onFileSelect,
  onFileClear,
  onRetry,
  onError,
  acceptedTypes,
  maxFileSizeInMb,
  id,
  texts,
}: LiliFileUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isHovered, setIsHovered] = useState(false);

  const typesToString = useMemo(() => acceptedTypes.join(','), [acceptedTypes]);

  const isFileTypeValid = (f: File) => {
    return acceptedTypes.some((type) => type.includes(f.type as LiliFilePickerFileType));
  };

  const isFileSizeValid = (f: File) => {
    if (!maxFileSizeInMb) return true;
    return f.size < maxFileSizeInMb * 1048576;
  };

  const handleFileSelection = (f: File | null) => {
    if (!f) return;
    if (!isFileTypeValid(f)) {
      onError?.(LiliFilePickerError.UNSUPPORTED_TYPE);
      return;
    }
    if (!isFileSizeValid(f)) {
      onError?.(LiliFilePickerError.FILE_TOO_BIG);
      return;
    }
    onFileSelect(f);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsHovered(false);
    if (state === 'disabled') return;
    handleFileSelection(e.dataTransfer.files.item(0));
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (state === 'default') setIsHovered(true);
  };

  const handleDragLeave = () => {
    setIsHovered(false);
  };

  const handleClick = () => {
    if (state === 'disabled' || state === 'uploading') return;
    fileInputRef.current?.click();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelection(e.target.files?.[0] || null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRetryClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRetry?.();
    fileInputRef.current?.click();
  };

  const handleClearClick = () => {
    onFileClear();
  };

  const visualState = state === 'default' && isHovered ? 'hover' : state;

  const containerClasses = (() => {
    const base =
      'relative rounded-[8px] border cursor-pointer transition-colors w-full h-full flex items-center justify-center';
    switch (visualState) {
      case 'hover':
        return `${base} border-outline-primary-bw-05 bg-surface-primary-bw-01`;
      case 'disabled':
        return `${base} border-outline-primary-bw-04 bg-text-main-primary-inverse cursor-not-allowed`;
      default:
        return `${base} border-outline-primary-bw-04 bg-text-main-primary-inverse`;
    }
  })();

  return (
    <div
      className={containerClasses}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={handleClick}
      onMouseEnter={() => state === 'default' && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid='file-upload'>
      <input
        ref={fileInputRef}
        type='file'
        accept={typesToString}
        onChange={handleInputChange}
        className='hidden'
        id={id}
        disabled={state === 'disabled'}
      />

      {(visualState === 'default' || visualState === 'hover') && <DefaultState texts={texts} />}
      {visualState === 'disabled' && <DisabledState texts={texts} />}
      {visualState === 'uploading' && <UploadingState fileName={file?.name} />}
      {visualState === 'uploaded' && (
        <UploadedState fileName={file?.name} onClear={handleClearClick} />
      )}
      {visualState === 'failed' && <FailedState texts={texts} onRetry={handleRetryClick} />}
    </div>
  );
};

const DefaultState = ({ texts }: { texts: LiliFileUploadTexts }) => (
  <div className='flex items-center justify-center gap-[16px] py-[32px] px-[16px]'>
    <img src={uploadIconUrl} alt='' className='shrink-0 w-[56px] h-[56px]' />
    <div className='flex flex-col gap-[2px]'>
      <Text.H9 fontWeight={FontWeight.SEMIBOLD} className='text-text-main-primary'>
        {texts.addAttachmentMobile ? (
          <>
            <span className='hidden sm:inline'>{texts.addAttachment}</span>
            <span className='sm:hidden'>{texts.addAttachmentMobile}</span>
          </>
        ) : (
          texts.addAttachment
        )}
      </Text.H9>
      <Text.H10 className='text-text-main-tertiary'>{texts.supportedFormats}</Text.H10>
    </div>
  </div>
);

const DisabledState = ({ texts }: { texts: LiliFileUploadTexts }) => (
  <div className='flex items-center justify-center gap-[16px] py-[32px] px-[16px] opacity-50'>
    <img src={uploadIconUrl} alt='' className='shrink-0 w-[56px] h-[56px]' />
    <div className='flex flex-col gap-[2px]'>
      <Text.H9 fontWeight={FontWeight.SEMIBOLD} className='text-text-main-disabled'>
        {texts.addAttachmentMobile ? (
          <>
            <span className='hidden sm:inline'>{texts.addAttachment}</span>
            <span className='sm:hidden'>{texts.addAttachmentMobile}</span>
          </>
        ) : (
          texts.addAttachment
        )}
      </Text.H9>
      <Text.H10 className='text-text-main-disabled'>{texts.supportedFormats}</Text.H10>
    </div>
  </div>
);

const UploadingState = ({ fileName }: { fileName?: string }) => (
  <div className='flex flex-col items-center justify-center gap-[8px] py-[32px] px-[16px]'>
    <Spinner height='24px' width='24px' spinnerColor={SpinnerColor.Blue} />
    {fileName && (
      <Text.H10 className='text-text-main-secondary truncate max-w-full'>{fileName}</Text.H10>
    )}
  </div>
);

const UploadedState = ({ fileName, onClear }: { fileName?: string; onClear: () => void }) => (
  <div className='flex items-center justify-between py-[32px] px-[24px] w-full'>
    <div className='flex items-center gap-[12px] min-w-0'>
      <img src={attachmentIconUrl} alt='' className='shrink-0 w-[29px] h-[36px]' />
      <Text.H9 id='selected-file__filename' className='text-text-main-primary truncate'>
        {fileName}
      </Text.H9>
    </div>
    <div onClick={(e) => e.stopPropagation()}>
      <LiliActionButton onClick={onClear} ariaLabel='Remove file'>
        <TrashIcon />
      </LiliActionButton>
    </div>
  </div>
);

const FailedState = ({
  texts,
  onRetry,
}: {
  texts: LiliFileUploadTexts;
  onRetry: (e: React.MouseEvent) => void;
}) => (
  <div className='flex items-center justify-center gap-[16px] py-[32px] px-[16px]'>
    <img src={uploadFailedIconUrl} alt='' className='shrink-0 w-[56px] h-[56px]' />
    <div className='flex flex-col gap-[2px]'>
      <Text.H9 fontWeight={FontWeight.SEMIBOLD} className='text-text-main-primary'>
        {texts.uploadFailed}
      </Text.H9>
      <button onClick={onRetry} className='text-left'>
        <Text.H10 className='text-text-brand-primary hover:text-text-brand-primary-hover'>
          {texts.uploadNewFile}
        </Text.H10>
      </button>
    </div>
  </div>
);
